/**
 * Derivaciones puras del Panorama (Sprint 286 D; REQ-MINT-006 §1).
 *
 * Solo indicadores de valor para el cliente final: catálogo, modelos nuevos,
 * rendimiento general de modelos, deprecaciones (lista) y noticias. Nunca
 * métricas internas/operativas (salud de fuentes, colas de curación,
 * mediciones propias, variaciones de precio) ni rendimiento de plataforma.
 */

import type { ModelListResponse } from "../types";

export interface PanoramaKpis {
  offerings: number;
  uniqueModels: number;
  deprecated: Array<{
    key: string;
    label: string;
    provider: string;
    status: string;
  }>;
}

/** Derivación pura de los KPIs de catálogo (nunca estima). */
export function derivePanoramaKpis(data?: ModelListResponse): PanoramaKpis {
  const items = data?.items ?? [];
  const unique = new Set<string>();
  const deprecated: PanoramaKpis["deprecated"] = [];
  for (const item of items) {
    if (item.offering.model_ref) unique.add(item.offering.model_ref);
    const lifecycle = (item.lifecycle_status ?? "").toLowerCase();
    if (lifecycle === "deprecated" || lifecycle === "retired") {
      deprecated.push({
        key: item.offering.sheet_id,
        label: item.offering.model_ref ?? item.offering.provider_model_id,
        provider: item.offering.provider,
        status: lifecycle,
      });
    }
  }
  return {
    offerings: items.length,
    uniqueModels: unique.size,
    deprecated,
  };
}

/** Fuente de las mediciones propias en ``mi_bench_results`` (backend ``OWN_SOURCE``). */
export const OWN_BENCH_SOURCE = "klviar-evals";

/** Solo benchmarks públicos: excluye mediciones propias y fixtures sintéticos. */
export function isPublicBenchmarkSource(source?: string | null): boolean {
  return !!source && source !== OWN_BENCH_SOURCE && !source.startsWith("fixture:");
}

/**
 * Puntuación pública de un ámbito (percentil 0–100) o ``null`` si no hay
 * componentes públicos trazables. Se excluyen las mediciones propias y los
 * fixtures sintéticos: el Panorama es de cliente y nunca estima.
 */
function publicDomainScore(domain: {
  score: number;
  components?: Array<{
    percentile: number;
    weight_effective: number;
    source: string;
  }>;
}): number | null {
  const components = domain.components ?? [];
  if (components.length === 0) return null;
  const external = components.filter((component) =>
    isPublicBenchmarkSource(component.source),
  );
  if (external.length === 0) return null;
  const weights = external.map((component) =>
    Number.isFinite(component.weight_effective)
      ? component.weight_effective
      : 0,
  );
  const totalWeight = weights.reduce((total, weight) => total + weight, 0);
  if (totalWeight > 0) {
    return external.reduce(
      (total, component, index) =>
        total + component.percentile * weights[index],
      0,
    ) / totalWeight;
  }
  const values = external
    .map((component) => component.percentile)
    .filter((value) => Number.isFinite(value));
  return values.length > 0
    ? values.reduce((total, value) => total + value, 0) / values.length
    : null;
}

export interface OverallPerformanceRow {
  modelRef: string;
  provider: string;
  /** Valor del rating global de referencia (Elo) o percentil de respaldo. */
  score: number;
  /** Unidad de ``score``: Elo del rating global o percentil 0–100. */
  unit: "elo" | "percentile";
  /** Número de ámbitos públicos con dato para el modelo. */
  domains: number;
}

/** Ratings globales de referencia, por orden de preferencia. */
const REFERENCE_BENCHMARKS = ["artificial_analysis_index", "lmarena_elo"] as const;

/**
 * Rendimiento general por modelo: media de los percentiles públicos por
 * ámbito, tomando el mejor valor por ámbito entre las ofertas del mismo
 * ``model_ref``. Solo benchmarks públicos (sin mediciones propias ni
 * fixtures); los huecos no se imputan.
 */
export function deriveOverallPerformance(
  data?: ModelListResponse,
): OverallPerformanceRow[] {
  const byModel = new Map<
    string,
    {
      provider: string;
      domains: Map<string, number>;
      reference: number | null;
      referenceCode: string | null;
    }
  >();
  for (const item of data?.items ?? []) {
    if (item.price_applicability === "not_applicable") continue;
    const modelRef = item.offering.model_ref;
    if (!modelRef) continue;
    const domains = item.benchmarks?.domains ?? [];
    if (domains.length === 0) continue;
    const entry =
      byModel.get(modelRef) ??
      {
        provider: item.offering.provider,
        domains: new Map<string, number>(),
        reference: null,
        referenceCode: null,
      };
    for (const domain of domains) {
      const score = publicDomainScore(domain);
      if (score !== null && Number.isFinite(score)) {
        const previous = entry.domains.get(domain.domain);
        if (previous === undefined || score > previous) {
          entry.domains.set(domain.domain, score);
        }
      }
      for (const component of domain.components ?? []) {
        if (
          isPublicBenchmarkSource(component.source) &&
          (REFERENCE_BENCHMARKS as readonly string[]).includes(
            component.benchmark_code,
          ) &&
          Number.isFinite(component.percentile)
        ) {
          if (entry.reference === null || component.value > entry.reference) {
            entry.reference = component.value;
            entry.referenceCode = component.benchmark_code;
          }
        }
      }
    }
    byModel.set(modelRef, entry);
  }
  return [...byModel.entries()]
    .map(([modelRef, entry]) => {
      const scores = [...entry.domains.values()];
      const fallback =
        scores.length > 0
          ? scores.reduce((total, value) => total + value, 0) / scores.length
          : 0;
      const hasReference = entry.reference !== null;
      const score = hasReference ? entry.reference! : fallback;
      return {
        modelRef,
        provider: entry.provider,
        score: Math.round(score * 10) / 10,
        unit: hasReference ? ("elo" as const) : ("percentile" as const),
        domains: scores.length,
      };
    })
    .filter((row) => row.domains > 0)
    .sort((a, b) => b.score - a.score);
}

// ---------------------------------------------------------------------------
// Habilidades (código, conversación, razonamiento)
// ---------------------------------------------------------------------------

export type SkillKey = "codigo" | "conversacion" | "razonamiento";

/** Habilidades que se presentan en el mapa y el ranking del Panorama. */
export const SKILLS: ReadonlyArray<{ key: SkillKey; labelKey: string }> = [
  { key: "codigo", labelKey: "modelIntel.panorama.skills.code" },
  { key: "conversacion", labelKey: "modelIntel.panorama.skills.conversation" },
  { key: "razonamiento", labelKey: "modelIntel.panorama.skills.reasoning" },
];

/** Benchmark LMArena que representa cada habilidad (Elo). */
export const SKILL_BENCHMARKS: Record<SkillKey, string> = {
  codigo: "lmarena_coding",
  conversacion: "lmarena_elo",
  razonamiento: "lmarena_math",
};

export interface SkillMatrixRow {
  modelRef: string;
  provider: string;
  overall: number | null;
  skills: Record<SkillKey, number | null>;
}

export interface SkillRankingRow {
  modelRef: string;
  provider: string;
  score: number;
  overall: number | null;
}

/** Mejor percentil por modelo y habilidad, sin duplicar ofertas. */
function skillScoresByModel(
  data?: ModelListResponse,
): Map<string, { provider: string; skills: Record<SkillKey, number | null> }> {
  const result = new Map<
    string,
    { provider: string; skills: Record<SkillKey, number | null> }
  >();
  for (const item of data?.items ?? []) {
    if (item.price_applicability === "not_applicable") continue;
    const modelRef = item.offering.model_ref;
    if (!modelRef) continue;
    const entry =
      result.get(modelRef) ??
      {
        provider: item.offering.provider,
        skills: { codigo: null, conversacion: null, razonamiento: null },
      };
    for (const domain of item.benchmarks?.domains ?? []) {
      if (!(domain.domain in entry.skills)) continue;
      const key = domain.domain as SkillKey;
      const reference = (domain.components ?? []).find(
        (component) =>
          component.benchmark_code === SKILL_BENCHMARKS[key] &&
          isPublicBenchmarkSource(component.source) &&
          Number.isFinite(component.value),
      );
      const score = reference?.value ?? (Number.isFinite(domain.score) ? domain.score : null);
      if (score === null) continue;
      const previous = entry.skills[key];
      if (previous === null || score > previous) {
        entry.skills[key] = score;
      }
    }
    result.set(modelRef, entry);
  }
  return result;
}

/** Filas del mapa: top N por rendimiento global, solo modelos con alguna habilidad. */
export function deriveSkillMatrix(
  data?: ModelListResponse,
  limit = 12,
): SkillMatrixRow[] {
  const overall = deriveOverallPerformance(data);
  const scores = skillScoresByModel(data);
  return overall
    .filter((row) => scores.has(row.modelRef))
    .slice(0, limit)
    .map((row) => {
      const entry = scores.get(row.modelRef)!;
      return {
        modelRef: row.modelRef,
        provider: entry.provider,
        overall: row.score,
        skills: entry.skills,
      };
    });
}

/** Ranking de una habilidad por percentil (mejor oferta por modelo). */
export function deriveSkillRanking(
  data: ModelListResponse | undefined,
  skill: SkillKey,
  limit = 10,
): SkillRankingRow[] {
  const overall = new Map(
    deriveOverallPerformance(data).map((row) => [row.modelRef, row.score]),
  );
  return [...skillScoresByModel(data).entries()]
    .flatMap(([modelRef, entry]) => {
      const score = entry.skills[skill];
      if (score === null) return [];
      return [
        {
          modelRef,
          provider: entry.provider,
          score: Math.round(score * 10) / 10,
          overall: overall.get(modelRef) ?? null,
        },
      ];
    })
    .sort((a, b) => b.score - a.score || (b.overall ?? 0) - (a.overall ?? 0))
    .slice(0, limit);
}
